import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { priceService } from '@services/priceService';
import type { Asset } from '@shared/types';

import { usePageVisibility } from './usePageVisibility';

/**
 * Hook that manages smart data recovery based on how long the app was inactive
 * Instead of pausing/resuming, it detects data gaps and backfills as needed
 */
export function useConnectionManager() {
  const { isVisible, timeBecameVisible, timeBecameHidden } =
    usePageVisibility();
  const queryClient = useQueryClient();
  const isInitialMount = useRef(true);
  const lastActiveTime = useRef<number>(Date.now());
  const recoveryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);

  // Keep WebSocket always running, but detect when we need to backfill data
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear any pending recovery timeout
    if (recoveryTimeoutRef.current) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }

    if (!isVisible && timeBecameHidden) {
      // App went to background - just record the time
      lastActiveTime.current = Date.now();
      console.log('[ConnectionManager] App backgrounded, recording timestamp');
    } else if (isVisible && timeBecameVisible) {
      // App came to foreground - check if we need to backfill data
      const now = Date.now();
      const inactiveTimeMs = now - lastActiveTime.current;
      const inactiveMinutes = Math.round(inactiveTimeMs / (1000 * 60));

      console.log(
        `[ConnectionManager] App resumed after ${inactiveMinutes}m inactive`,
      );

      // If we were inactive for more than 30 seconds, backfill data
      if (inactiveTimeMs > 30_000) {
        setIsRecovering(true);
        recoveryTimeoutRef.current = setTimeout(() => {
          recoverMissingData(inactiveTimeMs, queryClient, setIsRecovering);
          recoveryTimeoutRef.current = null;
        }, 500); // Small delay to let UI settle
      }

      lastActiveTime.current = now;
    }

    return () => {
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
        recoveryTimeoutRef.current = null;
      }
    };
  }, [isVisible, queryClient]);

  return {
    isVisible,
    isRecovering,
    getConnectionStats: () => priceService.getConnectionState(),
  };
}

/**
 * Smart data recovery based on how long we were inactive
 */
async function recoverMissingData(
  inactiveTimeMs: number,
  queryClient: any,
  setIsRecovering: (value: boolean) => void,
) {
  const inactiveMinutes = Math.round(inactiveTimeMs / (1000 * 60));
  const supportedAssets: Asset[] = ['BTC', 'ETH', 'SOL' /* , 'XRP' */];

  try {
    // The price service will automatically detect stale connections and reconnect
    console.log(
      `[Recovery] Starting data recovery after ${inactiveMinutes}m inactive`,
    );

    if (inactiveTimeMs < 10 * 60_000) {
      // Less than 10 minutes - refresh 30m of mixed data (5s + 1s)
      console.log(
        `[Recovery] Refreshing 30m mixed candles after ${inactiveMinutes}m gap`,
      );
      for (const asset of supportedAssets) {
        // Re-bootstrap to get fresh 30m mixed data (5s + 1s)
        await priceService.bootstrap([asset]);

        // Clear time range data cache to force refresh
        queryClient.invalidateQueries({ queryKey: ['timeRangeData', asset] });
      }
    } else {
      // More than 10 minutes - refresh both 30m mixed + 1m day candles
      console.log(
        `[Recovery] Refreshing 30m mixed + 1m day candles after ${inactiveMinutes}m gap`,
      );
      for (const asset of supportedAssets) {
        // Re-bootstrap to get fresh 30m mixed data (5s + 1s)
        await priceService.bootstrap([asset]);

        // Clear both time range and day candles cache
        queryClient.invalidateQueries({ queryKey: ['timeRangeData', asset] });
        queryClient.invalidateQueries({ queryKey: ['dayCandles', asset] });
      }
    }

    console.log(
      `[Recovery] Data recovery completed for ${inactiveMinutes}m gap`,
    );
  } catch (error) {
    console.warn('[Recovery] Failed to recover missing data:', error);
  } finally {
    setIsRecovering(false);
  }
}
